import React from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function GstConfig() {
  const [cfg, setCfg] = React.useState(null);
  const load = React.useCallback(async () => {
    setCfg((await api.get("/settings/gst")).data);
  }, []);
  React.useEffect(() => { load(); }, [load]);
  const save = async () => {
    try { await api.patch("/settings/gst", cfg); toast.success("GST configuration saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (!cfg) return <div className="text-muted-foreground p-4">Loading…</div>;
  const set = (patch) => setCfg({ ...cfg, ...patch });

  return (
    <div className="card-sharp p-5 space-y-4 max-w-3xl" data-testid="gst-config-panel">
      <div className="text-[10px] font-mono uppercase tracking-widest text-accent">GST Registration & Filing</div>

      <div className="grid grid-cols-2 gap-3">
        <div><Label>Company GSTIN</Label><Input value={cfg.gstin || ""} onChange={(e) => set({ gstin: e.target.value.toUpperCase() })} className="rounded-sm mt-1 font-mono" placeholder="15-char GSTIN" data-testid="gst-cfg-gstin" /></div>
        <div><Label>Company PAN</Label><Input value={cfg.pan || ""} onChange={(e) => set({ pan: e.target.value.toUpperCase() })} className="rounded-sm mt-1 font-mono" placeholder="10-char PAN" /></div>
        <div><Label>Filing frequency</Label>
          <Select value={cfg.filing_frequency} onValueChange={(v) => set({ filing_frequency: v })}>
            <SelectTrigger className="rounded-sm mt-1" data-testid="gst-cfg-frequency"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Monthly (GSTR-1 / 3B)</SelectItem>
              <SelectItem value="quarterly">Quarterly (QRMP scheme)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Default place of supply (state)</Label><Input value={cfg.default_place_of_supply || ""} onChange={(e) => set({ default_place_of_supply: e.target.value })} className="rounded-sm mt-1" placeholder="e.g. Maharashtra" /></div>
        <div><Label>E-Invoice threshold (₹ crore)</Label><Input type="number" value={cfg.einvoice_threshold_cr} onChange={(e) => set({ einvoice_threshold_cr: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums" /></div>
        <div><Label>E-Way threshold (₹)</Label><Input type="number" value={cfg.eway_threshold} onChange={(e) => set({ eway_threshold: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums" /></div>
      </div>

      <div className="border-t pt-4 space-y-3">
        <div className="flex items-center justify-between">
          <div><Label>Enable E-Invoice generation on invoice post</Label><div className="text-xs text-muted-foreground">Auto-generate IRN + QR</div></div>
          <Switch checked={!!cfg.einvoice_enabled} onCheckedChange={(v) => set({ einvoice_enabled: v })} data-testid="gst-cfg-einvoice" />
        </div>
        <div className="flex items-center justify-between">
          <div><Label>Sandbox environment</Label><div className="text-xs text-muted-foreground">Safe test mode via Sandbox.co.in</div></div>
          <Switch checked={!!cfg.sandbox_env} onCheckedChange={(v) => set({ sandbox_env: v })} />
        </div>
        <div className="flex items-center justify-between">
          <div><Label>Warn on reverse-charge purchases</Label><div className="text-xs text-muted-foreground">Prompt user when bill from unregistered supplier</div></div>
          <Switch checked={!!cfg.reverse_charge_notify} onCheckedChange={(v) => set({ reverse_charge_notify: v })} />
        </div>
        <div className="flex items-center justify-between">
          <div><Label>Require HSN/SAC on every line</Label><div className="text-xs text-muted-foreground">Mandatory for taxpayers &gt; ₹5 Cr turnover</div></div>
          <Switch checked={!!cfg.hsn_mandatory} onCheckedChange={(v) => set({ hsn_mandatory: v })} />
        </div>
      </div>

      {/* LUT — mandatory for export/SEZ WITHOUT payment of IGST (Rule 96A) */}
      <div className="border-t pt-4 space-y-3" data-testid="lut-section">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// LUT (Rule 96A) · Export without payment of tax</div>
            <div className="text-sm text-muted-foreground mt-1 leading-relaxed">
              A valid Letter of Undertaking is <b>required</b> to raise export / SEZ invoices
              WITHOUT payment of IGST. File LUT annually on the GST portal (Services →
              User Services → Furnish LUT) then paste the ARN + reference number here.
              Without a valid LUT on this page, the invoice creator will hard-block the
              save with a clear error.
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>LUT Reference Number</Label>
            <Input value={cfg.lut_number || ""} onChange={(e) => set({ lut_number: e.target.value })}
              className="rounded-sm mt-1 font-mono" placeholder="e.g. LUT-2025-26/0001" data-testid="lut-number" />
          </div>
          <div>
            <Label>LUT ARN</Label>
            <Input value={cfg.lut_arn || ""} onChange={(e) => set({ lut_arn: e.target.value })}
              className="rounded-sm mt-1 font-mono" placeholder="15-char ARN from GST portal" data-testid="lut-arn" />
          </div>
          <div>
            <Label>LUT Valid From</Label>
            <Input type="date" value={cfg.lut_valid_from || ""} onChange={(e) => set({ lut_valid_from: e.target.value })}
              className="rounded-sm mt-1" data-testid="lut-valid-from" />
          </div>
          <div>
            <Label>LUT Valid To</Label>
            <Input type="date" value={cfg.lut_valid_to || ""} onChange={(e) => set({ lut_valid_to: e.target.value })}
              className="rounded-sm mt-1" data-testid="lut-valid-to" />
            {cfg.lut_valid_to && cfg.lut_valid_to < new Date().toISOString().slice(0,10) && (
              <div className="text-[10px] text-red-500 mt-1">⚠ Expired — renew on the GST portal</div>
            )}
          </div>
          <div>
            <Label>Undertaking type</Label>
            <Select value={cfg.lut_bond_type || "lut"} onValueChange={(v) => set({ lut_bond_type: v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="lut-bond-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="lut">LUT (Letter of Undertaking)</SelectItem>
                <SelectItem value="bond">Bond (with bank guarantee)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="flex justify-end"><Button onClick={save} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="save-gst-cfg">Save GST configuration</Button></div>
    </div>
  );
}

/* -------------------------------------------------------------------------
 *  Extra sub-section: GST Portal Filing credentials + RCM / 17(5) toggles
 *  Rendered below the main configuration card via a small helper block that
 *  merges into the same `cfg` state. Keeping it inline (not a new file)
 *  because it's just 4 fields + 2 toggles — a full new page would be overkill.
 * ------------------------------------------------------------------------- */
